/**
 * HealthCheckAuditPage — /health-checks
 *
 * US-24-GR-15 — Paginated board-health sweep audit table with drill-down drawer.
 */

import { useState, useEffect, useCallback, startTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getHealthChecks,
  getHealthCheckDetail,
  type AgentHealthCheckRunDto,
  type HealthCheckDetail,
} from '../api/orchestratorClient';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { Table, type Column } from '../components/Table';
import { Drawer } from '../components/Drawer';
import {
  TimeWindowSelector,
  loadStoredWindow,
  saveStoredWindow,
  type TimeWindow,
} from '../components/TimeWindowSelector';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Bug 15179: the `*Count` fields are alerts SENT this run, not cards found — a stuck card
// still inside its alert cooldown contributes to neither `*Count` nor "all clear" honesty.
// "All clear" must be judged from the `*FoundCount` fields, which mirror what the drawer's
// details arrays actually contain.
function isAllClear(run: AgentHealthCheckRunDto): boolean {
  return (
    run.stuckInProgressFoundCount === 0 &&
    run.unreviewedReadyFoundCount === 0 &&
    run.overduePendingFoundCount === 0 &&
    run.orphanedJobsCount === 0 &&
    run.pendingStatusRetriesCount === 0 &&
    run.blockedRemindersCount === 0 &&
    run.creditDeductionRetriesCount === 0 &&
    run.notificationsSentCount === 0
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

interface DetailSection {
  label: string;
  ids: string[];
}

function HealthCheckDrawer({
  run,
  onClose,
}: {
  run: AgentHealthCheckRunDto | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<HealthCheckDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!run) return;
    let cancelled = false;
    startTransition(() => {
      setDetail(null);
      setError(null);
      setIsLoading(true);
    });
    getHealthCheckDetail(run.id)
      .then((result) => {
        if (!cancelled) setDetail(result.details);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load details');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [run]);

  const sections: DetailSection[] = detail
    ? [
        { label: 'Stuck In-Progress', ids: detail.stuckInProgress },
        { label: 'Unreviewed Ready', ids: detail.unreviewedReady },
        { label: 'Overdue Pending', ids: detail.overduePending },
        { label: 'Orphaned Jobs', ids: detail.orphanedJobs },
        { label: 'Pending Status Retries', ids: detail.pendingStatusRetries },
        { label: 'Blocked Reminders', ids: detail.blockedReminders },
        { label: 'Credit Deduction Retries', ids: detail.creditDeductionRetries },
      ].filter((s) => s.ids.length > 0)
    : [];

  const title = run
    ? `Health Check — ${formatDate(run.runAt)}`
    : 'Health Check Detail';

  return (
    <Drawer open={!!run} onClose={onClose} title={title}>
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-8 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded p-3 text-xs text-red-300">
          {error}
        </div>
      )}

      {!isLoading && !error && detail && sections.length === 0 && (
        <div className="flex items-center justify-center h-32 text-zinc-500 text-sm">
          All checks were clean for this sweep.
        </div>
      )}

      {!isLoading && !error && detail && sections.length > 0 && (
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.label}>
              <p className="text-xs font-semibold text-zinc-400 mb-2">
                {section.label} ({section.ids.length})
              </p>
              <ul className="space-y-1">
                {section.ids.map((id) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/jobs?jobId=${encodeURIComponent(id)}`)
                      }
                      className="text-xs font-mono text-violet-400 hover:text-violet-300"
                    >
                      {id}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function HealthCheckAuditPage() {
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(loadStoredWindow);
  const [runs, setRuns] = useState<AgentHealthCheckRunDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRun, setSelectedRun] = useState<AgentHealthCheckRunDto | null>(null);
  // Work item 17426: the API answers 200 with an empty list when the AgentHealthCheckRun query
  // fails, because the table genuinely may not exist in an environment. It now says which of the
  // two happened, so this page can stop reporting a failed read as "no sweeps recorded".
  const [readFailedReason, setReadFailedReason] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const fetchRuns = useCallback(async () => {
    const isCurrent = beginLoad();
    setIsLoading(true);
    setError(null);
    setReadFailedReason(null);
    try {
      const result = await getHealthChecks({ window: timeWindow, page, pageSize: 50 });
      if (!isCurrent()) return;
      setRuns(result.items);
      setTotal(result.total);
      setReadFailedReason(result.readFailed ? (result.readFailedReason ?? 'unknown reason') : null);
    } catch (err: unknown) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load health checks');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [beginLoad, timeWindow, page]);

  useEffect(() => {
    startTransition(() => { void fetchRuns(); });
  }, [fetchRuns]);

  function handleWindowChange(w: TimeWindow) {
    setTimeWindow(w);
    saveStoredWindow(w);
    setPage(1);
  }

  const columns: Column<AgentHealthCheckRunDto>[] = [
    {
      key: 'runAt',
      label: 'Run At',
      sortable: false,
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono">{formatDate(row.runAt)}</span>
          {isAllClear(row) && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              all clear
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'durationMs',
      label: 'Duration',
      render: (row) => (
        <span
          className={`text-xs font-mono ${row.durationMs > 60000 ? 'text-red-400 font-semibold' : 'text-zinc-400'}`}
        >
          {formatDuration(row.durationMs)}
        </span>
      ),
    },
    {
      key: 'stuckInProgressFoundCount',
      label: 'Stuck',
      render: (row) => <CountCell value={row.stuckInProgressFoundCount} />,
    },
    {
      key: 'unreviewedReadyFoundCount',
      label: 'Unreviewed',
      render: (row) => <CountCell value={row.unreviewedReadyFoundCount} />,
    },
    {
      key: 'overduePendingFoundCount',
      label: 'Overdue',
      render: (row) => <CountCell value={row.overduePendingFoundCount} />,
    },
    {
      key: 'orphanedJobsCount',
      label: 'Orphaned',
      render: (row) => <CountCell value={row.orphanedJobsCount} />,
    },
    {
      key: 'blockedRemindersCount',
      label: 'Blocked',
      render: (row) => <CountCell value={row.blockedRemindersCount} />,
    },
    {
      key: 'notificationsSentCount',
      label: 'Notifs',
      render: (row) => <CountCell value={row.notificationsSentCount} />,
    },
    {
      key: 'alertsSent',
      label: 'Alerts Sent',
      render: (row) => (
        <span className="text-xs font-mono text-zinc-500 flex gap-2">
          <span title="Stuck In-Progress alerts sent this run">S:{row.stuckInProgressCount}</span>
          <span title="Unreviewed Ready alerts sent this run">U:{row.unreviewedReadyCount}</span>
          <span title="Overdue Pending alerts sent this run">O:{row.overduePendingCount}</span>
        </span>
      ),
    },
  ];

  return (
    <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Filter bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-800 bg-zinc-950">
        <h1 className="text-sm font-semibold text-zinc-100">Health Check Audit</h1>
        <TimeWindowSelector value={timeWindow} onChange={handleWindowChange} />
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          className="px-6 py-2 bg-red-500/10 border-b border-red-500/20 text-red-300 text-xs"
        >
          {error}
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <Table
          columns={columns}
          rows={runs}
          keyFn={(row) => String(row.id)}
          onRowClick={(row) => setSelectedRun(row)}
          isLoading={isLoading}
          emptyMessage={
            readFailedReason ? (
              // Work item 17426: an unknown list and an empty one are different answers. Saying
              // "no sweeps recorded" when the read failed tells the operator the orchestrator is
              // idle, which is the opposite of what happened.
              <span className="text-amber-300">
                Health-check history unavailable — this list is unknown, not empty.
                <span className="block mt-1 text-zinc-500">{readFailedReason}</span>
              </span>
            ) : (
              <span>
                No health-check sweeps recorded. Confirm <code>OrchestratorService</code> is running.
                {!isLoading && (
                  <span className="block mt-1 text-zinc-600">
                    If US-24-GR-09 (health-check persistence) has not shipped yet,
                    this page will remain empty.
                  </span>
                )}
              </span>
            )
          }
          page={page}
          pageSize={50}
          total={total}
          onPageChange={setPage}
        />
      </div>

      {/* Drawer */}
      <HealthCheckDrawer run={selectedRun} onClose={() => setSelectedRun(null)} />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Small helper
// ---------------------------------------------------------------------------

function CountCell({ value }: { value: number }) {
  return (
    <span className={`text-xs ${value > 0 ? 'text-amber-400 font-medium' : 'text-zinc-600'}`}>
      {value}
    </span>
  );
}
